import React, { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Text,
  Textarea,
} from '@musa.tech/design-system';
import { DatePickerField, formatLocalDate, parseLocalDate } from '@/components/collects/sob-demanda/DatePickerField';
import { BucketLocationType, type BucketContract } from '@/integrations/agreements/client';
import { establishmentName, formatAddress, formatDay } from '@/utils/onDemandRequestRules';
import {
  BUCKET_LOCATION_LABELS,
  BUCKET_TYPE_DESCRIPTIONS,
  BUCKET_TYPE_LABELS,
  describeBucketAdvance,
  describeBucketStayLimits,
  describeBucketUsage,
} from '@/utils/bucketRequestRules';
import {
  MAX_BUCKET_NUMBER_LENGTH,
  MAX_BUCKETS_PER_REQUEST,
  withBucketQuantity,
  type BucketRequestFormErrors,
  type BucketRequestFormState,
} from './bucketRequestForm';
import { BUCKET_FIELD_TYPES, countBucketOperations, type BucketQuantityField } from './bucketQuantities';
import { PeriodSelect } from './PeriodSelect';
import { OnDemandPhotoField } from './OnDemandPhotoField';

/** Sem troca: para trocar, o gerador pede uma retirada e uma colocação. */
const FIELDS: BucketQuantityField[] = ['placement', 'removal'];
const LOCATIONS = [BucketLocationType.PUBLIC_ROAD, BucketLocationType.PRIVATE_AREA];

export interface BucketRequestDetailsStepProps {
  contract: BucketContract;
  form: BucketRequestFormState;
  onChange: (form: BucketRequestFormState) => void;
  /** Primeiro dia que a antecedência da regra permite (`YYYY-MM-DD`, São Paulo). */
  minDate: string;
  /** Por que a data mais cedo é a terça (piso semanal); nulo quando não há piso agora. */
  weeklyNote?: string | null;
  /**
   * Operação Musa: a antecedência não vale e `minDate` é o dia mais antigo aceito no passado, para
   * formalizar o que já aconteceu.
   */
  backdate?: boolean;
  /** Erros só aparecem depois da primeira tentativa de enviar. */
  errors: BucketRequestFormErrors;
  submitError: string | null;
  /** Operações do último envio que não entraram — as outras já foram criadas. */
  failures: Array<{ label: string; error: string }>;
  /** Quantas entraram no último envio, quando parte falhou. */
  partialSucceeded: number;
  /** Envio de foto em andamento: o wizard segura o pedido até a foto terminar de subir. */
  onPhotoUploadingChange: (uploading: boolean) => void;
}

export const BucketRequestDetailsStep: React.FC<BucketRequestDetailsStepProps> = ({
  contract,
  form,
  onChange,
  minDate,
  weeklyNote,
  backdate = false,
  errors,
  submitError,
  failures,
  partialSucceeded,
  onPhotoUploadingChange,
}) => {
  const update = (patch: Partial<BucketRequestFormState>) => onChange({ ...form, ...patch });

  const operations = countBucketOperations(form);
  // o número da retirada é sempre pedido: obrigatório no contrato que controla número, opcional nos outros
  const numberRequired = !!contract.requiresNumber;

  const setRemovalNumber = (index: number, value: string) =>
    update({ removalBucketNumbers: form.removalBucketNumbers.map((number, i) => (i === index ? value : number)) });

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryItem label="Estabelecimento" value={establishmentName(contract.generator)} detail={formatAddress(contract.address)} />
          <SummaryItem label="Acondicionamento" value={contract.packaging.name ?? '—'} detail={contract.material.name ?? undefined} />
          <SummaryItem
            label="No endereço"
            value={describeBucketUsage(contract)}
            detail={describeBucketStayLimits(contract)}
          />
          <SummaryItem
            label="Regra do pedido"
            value={backdate ? 'Data passada liberada para a operação Musa' : describeBucketAdvance(contract.rule)}
            detail={`Pedidos a partir de ${formatDay(minDate)}`}
          />
        </CardContent>
      </Card>

      {weeklyNote && (
        <Alert data-testid="weekly-floor-note">
          <AlertTitle>Datas a partir de terça-feira</AlertTitle>
          <AlertDescription>{weeklyNote}</AlertDescription>
        </Alert>
      )}

      {contract.aboveLimit && (
        <Alert>
          <AlertTitle>Contrato acima do limite de caçambas</AlertTitle>
          <AlertDescription>
            Contando as que estão no endereço e as já planejadas, o contrato passa do limite de{' '}
            {contract.simultaneousLimit} ao mesmo tempo. O pedido é aceito mesmo assim — a Musa
            acompanha o excedente.
          </AlertDescription>
        </Alert>
      )}

      {submitError && (
        <Alert variant="destructive" data-testid="bucket-submit-error">
          <AlertTitle>O pedido não foi criado</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      {failures.length > 0 && (
        <Alert variant="destructive" data-testid="bucket-batch-failures">
          <AlertTitle>
            {partialSucceeded > 0
              ? `${partialSucceeded} ${partialSucceeded === 1 ? 'pedido entrou' : 'pedidos entraram'}; ${failures.length} não`
              : 'Nenhum pedido entrou'}
          </AlertTitle>
          <AlertDescription>
            <ul className="mt-1 list-disc pl-4">
              {failures.map((failure, index) => (
                <li key={index}>
                  {failure.label}: {failure.error}
                </li>
              ))}
            </ul>
            {partialSucceeded > 0 && (
              <Text variant="body" size="small" className="mt-2">
                Os que entraram já estão com o transportador. O formulário ficou só com o que falta —
                corrija e envie de novo.
              </Text>
            )}
          </AlertDescription>
        </Alert>
      )}

      <section className="flex flex-col gap-3">
        <SectionTitle
          title="Operações"
          description="Quantas caçambas o transportador vai colocar e quantas vai retirar. Para trocar uma caçamba, peça uma retirada e uma colocação."
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FIELDS.map((field) => (
            <QuantityField
              key={field}
              field={field}
              value={form[field]}
              onCommit={(value) => onChange(withBucketQuantity(form, field, value))}
            />
          ))}
        </div>
        <Text variant="body" size="small" className="text-neutral-500" data-testid="bucket-quantities-summary">
          Neste pedido: {operations} {operations === 1 ? 'operação' : 'operações'}.
        </Text>
        {errors.quantities && <FieldError message={errors.quantities} />}

        {form.removal > 0 && (
          <div className="flex flex-col gap-3" data-testid="bucket-removal-numbers">
            <div className="flex flex-col gap-0.5">
              <Text as="h4" variant="label" size="small" className="text-neutral-900">
                {form.removal > 1 ? 'Caçambas a retirar' : 'Caçamba a retirar'}
              </Text>
              <Text variant="body" size="small" className="text-neutral-500">
                {numberRequired
                  ? 'Este contrato controla o número de cada caçamba. Informe o número da que vai sair.'
                  : 'Se souber, informe o número da caçamba que vai sair. Sem ele, o transportador informa no registro.'}
              </Text>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {form.removalBucketNumbers.map((number, index) => (
                <Field
                  key={index}
                  label={removalNumberLabel(index, form.removal, numberRequired)}
                  htmlFor={`bucket-removal-number-${index}`}
                  error={errors.removalBucketNumbers?.[index]}
                >
                  <Input
                    id={`bucket-removal-number-${index}`}
                    placeholder="Ex.: 4521"
                    maxLength={MAX_BUCKET_NUMBER_LENGTH}
                    value={number}
                    onChange={(e) => setRemovalNumber(index, e.target.value)}
                  />
                </Field>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <SectionTitle
          title="Quando"
          description={
            backdate
              ? 'A data em que as operações aconteceram ou devem acontecer. Para outro dia, faça outro pedido.'
              : 'A data em que as operações devem acontecer. Para outro dia, faça outro pedido.'
          }
        />
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[220px_200px]">
          <Field label="Data" error={errors.plannedDate}>
            <DatePickerField
              value={form.plannedDate ? parseLocalDate(form.plannedDate) : null}
              onChange={(date) => update({ plannedDate: date ? formatLocalDate(date) : '' })}
              minDate={minDate}
              allowSunday
            />
          </Field>
          <Field label="Turno">
            <PeriodSelect
              value={form.period}
              onChange={(period) => update({ period })}
              ariaLabel="Turno da operação"
            />
          </Field>
        </div>

        {form.placement > 0 && (
          <Field label={form.placement > 1 ? 'Onde as caçambas novas ficam' : 'Onde a caçamba nova fica'} error={errors.locationType}>
            <RadioGroup
              value={form.locationType != null ? String(form.locationType) : ''}
              onValueChange={(value) => update({ locationType: Number(value) as BucketLocationType })}
              className="flex flex-wrap gap-4"
            >
              {LOCATIONS.map((location) => (
                <div key={location} className="flex items-center gap-2">
                  <RadioGroupItem value={String(location)} id={`bucket-location-${location}`} />
                  <Label htmlFor={`bucket-location-${location}`} className="cursor-pointer">
                    <Text variant="body" size="small" className="text-neutral-800">
                      {BUCKET_LOCATION_LABELS[location]}
                    </Text>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </Field>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <SectionTitle title="Quem acompanha" description="Contato que o transportador procura no dia." />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Nome" htmlFor="bucket-contact-name" error={errors.contactName}>
            <Input
              id="bucket-contact-name"
              value={form.contactName}
              onChange={(e) => update({ contactName: e.target.value })}
            />
          </Field>
          <Field label="Telefone" htmlFor="bucket-contact-phone" error={errors.contactPhone}>
            <Input
              id="bucket-contact-phone"
              type="tel"
              placeholder="(11) 91234-5678"
              value={form.contactPhone}
              onChange={(e) => update({ contactPhone: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Observações (opcional)" htmlFor="bucket-observations" error={errors.observations}>
          <Textarea
            id="bucket-observations"
            rows={3}
            placeholder="Ex.: deixar ao lado do portão de serviço"
            value={form.observations}
            onChange={(e) => update({ observations: e.target.value })}
          />
        </Field>
      </section>

      {/* o gerador manda ao menos uma foto (EDD-569); a operação Musa, que pode pedir com data passada, não */}
      <OnDemandPhotoField
        service="bucket"
        title={backdate ? 'Fotos (opcional)' : 'Fotos'}
        description={backdate ? undefined : 'Pelo menos uma foto do local: onde a caçamba vai ficar ou a que vai sair.'}
        photoUrls={form.photoUrls}
        onChange={(photoUrls) => update({ photoUrls })}
        onUploadingChange={onPhotoUploadingChange}
      >
        {errors.photos && <FieldError message={errors.photos} />}
      </OnDemandPhotoField>
    </div>
  );
};

/** "Número da caçamba", "Número da caçamba (retirada 2)" — com "opcional" quando o contrato não exige. */
function removalNumberLabel(index: number, removals: number, required: boolean): string {
  const details = [removals > 1 ? `retirada ${index + 1}` : null, required ? null : 'opcional'].filter(Boolean);
  return details.length ? `Número da caçamba (${details.join(', ')})` : 'Número da caçamba';
}

/**
 * O valor só normaliza quando a pessoa sai do campo: normalizar a cada tecla mexeria nos campos
 * de número no meio da digitação (o "1" de "14" já cortaria os números das retiradas).
 */
const QuantityField: React.FC<{
  field: BucketQuantityField;
  value: number;
  onCommit: (value: string) => void;
}> = ({ field, value, onCommit }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const type = BUCKET_FIELD_TYPES[field];
  const id = `bucket-quantity-${field}`;

  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    onCommit(draft);
  };

  return (
    <div className="flex flex-col gap-1 rounded-md border border-border p-3">
      <Label htmlFor={id}>
        <Text variant="label" size="small" className="text-neutral-700">
          {BUCKET_TYPE_LABELS[type]}
        </Text>
      </Label>
      <Text variant="body" size="small" className="text-neutral-500">
        {BUCKET_TYPE_DESCRIPTIONS[type]}
      </Text>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        max={MAX_BUCKETS_PER_REQUEST}
        step={1}
        className="mt-1 max-w-[120px]"
        value={draft ?? String(value)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
        }}
      />
    </div>
  );
};

const SummaryItem: React.FC<{ label: string; value: string; detail?: string }> = ({ label, value, detail }) => (
  <div className="flex flex-col gap-0.5">
    <Text variant="label" size="small" className="text-neutral-500">
      {label}
    </Text>
    <Text variant="body" size="small" className="font-medium text-neutral-900">
      {value}
    </Text>
    {detail && (
      <Text variant="body" size="small" className="text-neutral-500">
        {detail}
      </Text>
    )}
  </div>
);

const SectionTitle: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="flex flex-col gap-0.5">
    <Text as="h3" variant="label" size="medium" className="text-neutral-900">
      {title}
    </Text>
    <Text variant="body" size="small" className="text-neutral-500">
      {description}
    </Text>
  </div>
);

const Field: React.FC<{ label: string; htmlFor?: string; error?: string; children: React.ReactNode }> = ({
  label,
  htmlFor,
  error,
  children,
}) => (
  <div className="flex flex-col gap-1">
    <Label htmlFor={htmlFor}>
      <Text variant="label" size="small" className="text-neutral-700">
        {label}
      </Text>
    </Label>
    {children}
    {error && <FieldError message={error} />}
  </div>
);

const FieldError: React.FC<{ message: string }> = ({ message }) => (
  <Text variant="body" size="small" className="text-error-500" role="alert">
    {message}
  </Text>
);
